import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import customDate from "../../../utils/customDate";
import { ArrowBack, CheckCircle } from "@mui/icons-material";
import { Chair, FlightTakeoff, Luggage, Restaurant } from "@mui/icons-material";
import customTime from "../../../utils/customTime";
import {
  getAirArabiaPricing,
  getBaggageDetails,
  getMealDetails,
  getSeatDetails,
} from "../Service/AirarabiaApi";
import Loading from "../../Loading";
import { formatNumber } from "../../../utils/convertToNumber";
import Ancillaries from "./components/Ancillaries";
import PriceSidebar from "./components/PriceSidebar";

const BasicFlightPricing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const passengersData = location.state?.passengersData;
  const originalPath = location.state?.originalPath;
  const selectedBundle = location.state?.selectedBundle;
  const passengers = location.state?.passengers;
  const pricingResponse = location.state?.response;
  const selectedOutboundBundle = location.state?.selectedOutboundBundle;
  const selectedInboundBundle = location.state?.selectedInboundBundle;
  const pricingRequest = location.state?.pricingRequest;
  const queryURL = location.state?.queryURL;
  const charges = location.state?.charges;
  const currency = location.state?.currency;
  const [isBaggageModalOpen, setIsBaggageModalOpen] = useState(false);
  const [baggageDetails, setBaggageDetails] = useState(null);
  const [selectedBaggage, setSelectedBaggage] = useState({});

  const [isMealModalOpen, setIsMealModalOpen] = useState(false);
  const [mealDetails, setMealDetails] = useState(null);
  const [selectedMeals, setSelectedMeals] = useState({});

  const [isSeatModalOpen, setIsSeatModalOpen] = useState(false);
  const [seatDetails, setSeatDetails] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState({});
  const [activePassengerTab, setActivePassengerTab] = useState(0);
  const [activeJourneyTab, setActiveJourneyTab] = useState(0);

  const [autoSelectedSeats, setAutoSelectedSeats] = useState(false);
  const [activeSeatTab, setActiveSeatTab] = useState(0);
  const [activeMealSegmentTab, setActiveMealSegmentTab] = useState(0);

  const [pricing, setPricing] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasPrefetched, setHasPrefetched] = useState(false);

  useEffect(() => {
    if (!hasPrefetched && pricingResponse) {
      setHasPrefetched(true);
      prefetchAllAncillaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPrefetched, pricingResponse]);

  useEffect(() => {
    if (pricing) {
      navigate(`/price-with-ancillaries${queryURL}`, {
        state: {
          passengersData,
          originalPath,
          response: pricing,
          sessionInfo: pricing?.sessionInfo,
          selectedBundle,
          selectedOutboundBundle,
          selectedInboundBundle,
          queryURL,
          charges: charges || 0,
          currency,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing]);

  const fetchPricingWithAncillaries = async () => {
    setIsLoading(true);

    const ancillaries = {
      baggage: [],
      meals: [],
      seats: [],
    };

    Object.entries(selectedBaggage).forEach(([, segments]) => {
      Object.entries(segments).forEach(([, baggage]) => {
        ancillaries.baggage.push({
          baggageCode: baggage.baggageCode,
          TravelerRefNumberRPHList: baggage.TravelerRefNumberRPHList,
          FlightRefNumberRPHList: baggage.rph,
          DepartureDate: baggage.DepartureDate,
          FlightNumber: baggage.FlightNumber,
        });
      });
    });

    // Process meals for all passengers
    Object.entries(selectedMeals).forEach(([, segments]) => {
      Object.entries(segments).forEach(([, meal]) => {
        ancillaries.meals.push({
          mealCode: meal.mealCode,
          mealQuantity: meal.mealQuantity || 1,
          TravelerRefNumberRPHList: meal.TravelerRefNumberRPHList,
          FlightRefNumberRPHList: pricingResponse.flights
            ?.map(
              (flight) =>
                flight?.flightNumber === meal.FlightNumber && flight?.rph
            )
            .filter(Boolean)[0],
          DepartureDate: meal.DepartureDate,
          FlightNumber: meal.FlightNumber,
        });
      });
    });

    // Process seats for all passengers
    Object.entries(selectedSeats).forEach(([, segments]) => {
      Object.entries(segments).forEach(([, seat]) => {
        ancillaries.seats.push({
          SeatNumber: seat.seatId,
          TravelerRefNumberRPHList: seat.TravelerRefNumberRPHList,
          FlightRefNumberRPHList: pricingResponse.flights
            ?.map(
              (flight) =>
                flight?.flightNumber === seat.FlightNumber && flight?.rph
            )
            .filter(Boolean)[0],
          DepartureDate: seat.DepartureDate,
          FlightNumber: seat.FlightNumber,
        });
      });
    });

    const requestData = {
      ...pricingRequest,
      passengers: {
        ADT: passengersData.ADT,
        CHD: passengersData.CHD,
        INF: passengersData.INF,
      },
      ancillaries: ancillaries,
      // bundleServices: {
      //   outboundBundleId:
      //     originalPath.length > 1
      //       ? selectedOutboundBundle?.id
      //       : selectedBundle?.id,
      //   ...(selectedInboundBundle?.id && {
      //     inboundBundleId: selectedInboundBundle.id,
      //   }),
      // },
      transactionIdentifier: pricingResponse?.transactionIdentifier,
      jsessionId: pricingResponse?.sessionInfo?.jsessionId,
      type: "ancillaries",
    };

    const response = await getAirArabiaPricing(requestData, setIsLoading);

    if (response.data.success) {
      setPricing(response.data);
    } else {
      setError("Failed to fetch updated pricing data");
    }
  };

  const getSegmentPath = (segment) => {
    const departureCode =
      segment?.departureAirport?.locationCode ||
      segment?.departureAirport?.code ||
      segment?.origin ||
      "N/A";

    const arrivalCode =
      segment?.arrivalAirport?.locationCode ||
      segment?.arrivalAirport?.code ||
      segment?.destination ||
      "N/A";

    return `${departureCode}-${arrivalCode}`;
  };

  const isAllBaggageSelected = () => {
    const segments = pricingResponse?.flights || [];
    return passengers?.every((passenger, passengerIndex) =>
      segments.every(
        (_, segmentIndex) =>
          selectedBaggage[passengerIndex]?.[segmentIndex]?.baggageCode
      )
    );
  };

  const isAllMealsSelected = () => {
    const segments = pricingResponse?.flights || [];
    return passengers?.every((passenger, passengerIndex) =>
      segments.every(
        (_, segmentIndex) =>
          selectedMeals[passengerIndex]?.[segmentIndex]?.mealCode
      )
    );
  };

  const isAllSeatsSelected = () => {
    const segments = pricingResponse?.flights || [];
    return passengers?.every((passenger, passengerIndex) =>
      segments.every(
        (_, segmentIndex) =>
          selectedSeats[passengerIndex]?.[segmentIndex]?.seatId
      )
    );
  };

  const handleProceedToBooking = () => {
    const errors = [];
    const segments = pricingResponse?.flights || [];

    passengers?.forEach((passenger, passengerIndex) => {
      segments.forEach((segment, segmentIndex) => {
        const segmentPath = getSegmentPath(segment);

        // Check baggage selection
        if (!selectedBaggage[passengerIndex]?.[segmentIndex]?.baggageCode) {
          errors.push(
            `Passenger ${
              passengerIndex + 1
            } - ${segmentPath}: Please select baggage`
          );
        }

        // Check meal selection
        if (!selectedMeals[passengerIndex]?.[segmentIndex]?.mealCode) {
          errors.push(
            `Passenger ${
              passengerIndex + 1
            } - ${segmentPath}: Please select a meal`
          );
        }

        // Check seat selection
        if (!selectedSeats[passengerIndex]?.[segmentIndex]?.seatId) {
          errors.push(
            `Passenger ${
              passengerIndex + 1
            } - ${segmentPath}: Please select a seat`
          );
        }
      });
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setValidationErrors([]);

    fetchPricingWithAncillaries();
    if (pricing) {
      navigate(`/price-with-ancillaries${queryURL}`, {
        state: {
          passengersData,
          originalPath,
          response: pricing,
          sessionInfo: pricing?.sessionInfo,
          selectedBundle,
          selectedOutboundBundle,
          selectedInboundBundle,
          queryURL,
          charges: charges || 0,
          currency,
        },
      });
    }
  };

  const formatCurrency = (amount, currency) => {
    if (amount === 0 || amount < 0.01) {
      return "Free";
    }
    return `${currency} ${amount?.toFixed(2)}`;
  };

  const totalTax = pricingResponse?.pricing?.passengers
    ?.reduce((acc, item) => {
      const taxSum =
        item.taxes?.reduce((taxAcc, tax) => taxAcc + Number(tax.amount), 0) ||
        0;
      const quantity = item.passengerType?.quantity || 1;
      return Number(acc + taxSum * quantity);
    }, 0)
    .toFixed(2);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-medium text-gray-800">
            {error || "Unable to load bundle pricing"}
          </h2>
          <p className="mb-4 text-gray-600">Please try again later.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-white rounded-lg transition-colors bg-main hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const flightSegments = pricingResponse?.flights;
  const pricingInfo = pricingResponse?.pricing?.totals;

  const fetchBaggageDetails = async () => {
    const flightSegments = pricingResponse?.flights;
    const request = {
      flightSegments: flightSegments.map((flight) => ({
        travelerRefNumberRPHs: "A1",
        flightNumber: flight?.flightNumber,
        departureDateTime: flight?.departureDateTime,
        arrivalDateTime: flight?.arrivalDateTime,
        rph: flight?.rph,
        returnFlag: "false",
        departureAirport: flight?.departureAirport,
        arrivalAirport: flight?.arrivalAirport,
        operatingAirline: { code: "G9" },
      })),
      transactionIdentifier: pricingResponse?.transactionIdentifier,
      jsessionId:
        pricingResponse?.sessionInfo?.jsessionId ||
        pricingResponse?.sessionInfo?.jsessionId,
    };

    const response = await getBaggageDetails(request, setIsLoading);
    console.log({ response });
    if (response.data.success) {
      setBaggageDetails(response.data.baggageDetails);
    }
  };

  const fetchMealDetails = async () => {
    const flightSegments = pricingResponse?.flights;
    const request = {
      flightSegments: flightSegments.map((flight) => ({
        travelerRefNumberRPHs: "A1",
        flightNumber: flight?.flightNumber,
        departureDateTime: flight?.departureDateTime,
        arrivalDateTime: flight?.arrivalDateTime,
        rph: flight?.rph,
        returnFlag: "false",
        departureAirport: flight?.departureAirport,
        arrivalAirport: flight?.arrivalAirport,
        operatingAirline: { code: "G9" },
      })),
      transactionIdentifier: pricingResponse?.transactionIdentifier,
      jsessionId:
        pricingResponse?.sessionInfo?.jsessionId ||
        pricingResponse?.sessionInfo?.jsessionId,
    };
    const response = await getMealDetails(request, setIsLoading);
    if (response.data.success) {
      setMealDetails(response.data.mealDetails);
    }
  };

  const fetchSeatDetails = async () => {
    const flightSegments = pricingResponse?.flights;
    const request = {
      flightSegments: flightSegments.map((flight) => ({
        travelerRefNumberRPHs: "A1",
        flightNumber: flight?.flightNumber,
        departureDateTime: flight?.departureDateTime,
        arrivalDateTime: flight?.arrivalDateTime,
        rph: flight?.rph,
        returnFlag: "false",
        departureAirport: flight?.departureAirport,
        arrivalAirport: flight?.arrivalAirport,
        operatingAirline: { code: "G9" },
      })),
      transactionIdentifier: pricingResponse?.transactionIdentifier,
      jsessionId:
        pricingResponse?.sessionInfo?.jsessionId ||
        pricingResponse?.sessionInfo?.jsessionId,
    };
    const response = await getSeatDetails(request, setIsLoading);
    if (response.data.success) {
      setSeatDetails(response.data);

      if (selectedBundle === null) {
        autoSelectLowestVACSeat(response.data);
        setAutoSelectedSeats(true);
      }
    }
  };

  const autoSelectLowestVACSeat = (seatData) => {
    const newSelectedSeats = { ...selectedSeats };
    const selectedSeatsPerSegment = {};

    passengers?.forEach((passenger, passengerIndex) => {
      if (!newSelectedSeats[passengerIndex]) {
        newSelectedSeats[passengerIndex] = {};
      }

      seatData.seatSelection?.forEach((selection, segmentIdx) => {
        if (newSelectedSeats[passengerIndex]?.[segmentIdx]?.seatId) {
          return;
        }

        if (!selectedSeatsPerSegment[segmentIdx]) {
          selectedSeatsPerSegment[segmentIdx] = new Set();
        }

        let lowestPricedSeat = null;
        let lowestPrice = Infinity;

        selection.cabins?.forEach((cabin) => {
          cabin.rows?.forEach((row) => {
            row.seats?.forEach((seat) => {
              if (seat.seatAvailability === "VAC") {
                const seatId = `${row.rowNumber}${seat.seatNumber}`;

                if (selectedSeatsPerSegment[segmentIdx].has(seatId)) {
                  return;
                }

                const price = parseFloat(seat.seatCharacteristics) || 0;

                if (price < lowestPrice) {
                  lowestPrice = price;
                  lowestPricedSeat = {
                    seat,
                    row,
                    cabin,
                    selection,
                    seatId,
                  };
                }
              }
            });
          });
        });

        if (lowestPricedSeat) {
          const { seat, row, selection: sel, seatId } = lowestPricedSeat;

          selectedSeatsPerSegment[segmentIdx].add(seatId);

          newSelectedSeats[passengerIndex][segmentIdx] = {
            ...seat,
            seatId,
            rowNumber: row.rowNumber,
            seatNumber: seat.seatNumber,
            rph: sel.rph,
            DepartureDate: sel.departureDateTime,
            FlightNumber: sel.flightNumber,
            passengerIndex,
            currencyCode: seat.currencyCode || "ETB",
            TravelerRefNumberRPHList: passengers?.[passengerIndex]?.refNumber,
          };
        }
      });
    });

    setSelectedSeats(newSelectedSeats);
  };

  const autoSelectLowestBaggage = (baggageData) => {
    const newSelectedBaggage = { ...selectedBaggage };

    passengers?.forEach((passenger, passengerIndex) => {
      if (!newSelectedBaggage[passengerIndex]) {
        newSelectedBaggage[passengerIndex] = {};
      }

      let currentGlobalSegmentIdx = 0;
      baggageData?.forEach((detail) => {
        let lowestPricedOption = null;
        let lowestPrice = Infinity;

        detail.baggageOptions?.forEach((option) => {
          const price = parseFloat(option.baggageCharge) || 0;
          if (price < lowestPrice) {
            lowestPrice = price;
            lowestPricedOption = option;
          }
        });

        if (lowestPricedOption) {
          detail.flightSegments?.forEach((segment, segmentIdx) => {
            const globalIdx = currentGlobalSegmentIdx + segmentIdx;
            newSelectedBaggage[passengerIndex][globalIdx] = {
              ...lowestPricedOption,
              rph: segment?.rph,
              DepartureDate: segment?.departureDateTime,
              FlightNumber: segment?.flightNumber,
              passengerIndex,
              TravelerRefNumberRPHList: passengers?.[passengerIndex]?.refNumber,
            };
          });
        }
        currentGlobalSegmentIdx += detail.flightSegments?.length || 0;
      });
    });

    setSelectedBaggage(newSelectedBaggage);
  };

  const autoSelectLowestMeal = (mealData) => {
    const newSelectedMeals = { ...selectedMeals };

    passengers?.forEach((passenger, passengerIndex) => {
      if (!newSelectedMeals[passengerIndex]) {
        newSelectedMeals[passengerIndex] = {};
      }

      mealData?.forEach((detail, segmentIdx) => {
        let lowestPricedMeal = null;
        let lowestPrice = Infinity;

        detail.meals?.forEach((meal) => {
          const price = parseFloat(meal.mealCharge) || 0;
          if (price < lowestPrice) {
            lowestPrice = price;
            lowestPricedMeal = meal;
          }
        });

        if (lowestPricedMeal) {
          newSelectedMeals[passengerIndex][segmentIdx] = {
            ...lowestPricedMeal,
            rph: detail.rph,
            DepartureDate: detail.departureDateTime,
            FlightNumber: detail.flightNumber,
            passengerIndex,
            TravelerRefNumberRPHList: passengers?.[passengerIndex]?.refNumber,
          };
        }
      });
    });

    setSelectedMeals(newSelectedMeals);
  };

  const prefetchAllAncillaries = async () => {
    const flightSegments = pricingResponse?.flights;
    const request = {
      flightSegments: flightSegments.map((flight) => ({
        travelerRefNumberRPHs: "A1",
        flightNumber: flight?.flightNumber,
        departureDateTime: flight?.departureDateTime,
        arrivalDateTime: flight?.arrivalDateTime,
        rph: flight?.rph,
        returnFlag: "false",
        departureAirport: flight?.departureAirport,
        arrivalAirport: flight?.arrivalAirport,
        operatingAirline: { code: "G9" },
      })),
      transactionIdentifier: pricingResponse?.transactionIdentifier,
      jsessionId:
        pricingResponse?.sessionInfo?.jsessionId ||
        pricingResponse?.sessionInfo?.jsessionId,
    };

    try {
      const baggageResponse = await getBaggageDetails(request, setIsLoading);
      if (baggageResponse.data.success) {
        setBaggageDetails(baggageResponse.data.baggageDetails);
        autoSelectLowestBaggage(baggageResponse.data.baggageDetails);
      }

      const mealResponse = await getMealDetails(request, setIsLoading);
      if (mealResponse.data.success) {
        setMealDetails(mealResponse.data.mealDetails);
        autoSelectLowestMeal(mealResponse.data.mealDetails);
      }

      const seatResponse = await getSeatDetails(request, setIsLoading);
      if (seatResponse.data.success) {
        setSeatDetails(seatResponse.data);
        autoSelectLowestVACSeat(seatResponse.data);
        setAutoSelectedSeats(true);
      }
    } catch (error) {
      console.error("Error prefetching ancillaries:", error);
    }
  };

  const totalPrice = () => {
    const totalFare =
      pricingInfo?.totalFare?.totalFare?.amount ||
      pricingResponse?.pricing?.totals?.totalFare?.amount;
    const total = totalFare + (charges || 0);

    return Number(total.toFixed(2));
  };

  return (
    <div className="px-4 py-2 min-h-screen">
      <div className="mx-auto mt-2 max-w-6xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 transition-colors text-main hover:text-main-800"
        >
          <ArrowBack className="w-5 h-5" />
          <span>Back to Flight Details</span>
        </button>

        <div className="mb-2 text-center">
          <h1 className="mb-2 text-3xl font-semibold text-gray-900">
            Bundle Pricing Confirmation
          </h1>
          <p className="text-lg text-gray-600">
            Review your selected bundle and pricing details
          </p>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="p-4 mb-6 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Please complete your selections
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="pl-5 space-y-1 list-disc">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="px-6 py-3 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h2 className="flex items-center mb-2 text-xl font-semibold text-gray-900">
                <FlightTakeoff className="mr-2 w-5 h-5 text-main" />
                Flight Summary
              </h2>

              {flightSegments?.map((flight, index) => (
                <div key={index} className="px-4 py-2 rounded-lg last:mb-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Flight {flight?.flightNumber}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {flight?.departureDateTime &&
                          customDate(flight?.departureDateTime)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">
                        {flight?.departureAirport?.code ||
                          flight?.departureAirport?.locationCode}{" "}
                        →{" "}
                        {flight?.arrivalAirport?.code ||
                          flight?.arrivalAirport?.locationCode}
                      </div>
                      {flight?.departureDateTime && flight?.arrivalDateTime && (
                        <div className="text-lg font-medium text-main">
                          {customTime(flight?.departureDateTime)} -{" "}
                          {customTime(flight?.arrivalDateTime)}
                        </div>
                      )}
                      {flight?.departureAirport?.terminal && (
                        <div className="text-xs text-gray-500">
                          Terminal: {flight?.departureAirport?.terminal}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {isAllBaggageSelected() &&
              isAllMealsSelected() &&
              isAllSeatsSelected() && (
                <div className="px-6 py-4 mt-4 bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center">
                    <h3 className="flex items-center text-xl font-semibold text-gray-900">
                      <CheckCircle className="mr-2 w-5 h-5 text-green-600" />
                      Ancillaries Selection Complete
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    All ancillaries have been automatically selected. If you
                    would like to modify your selections, you can do so using
                    the options below.
                  </p>
                </div>
              )}

            <div className="p-6 mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                <Luggage className="mr-2 w-5 h-5 text-main-900" />
                Add Ancillaries
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <button
                  onClick={() => {
                    setIsBaggageModalOpen(true);
                    fetchBaggageDetails();
                    setActivePassengerTab(0);
                  }}
                  className={`flex justify-center items-center px-4 py-3 rounded-lg transition-colors ${
                    isAllBaggageSelected()
                      ? "text-green-800 bg-green-100 hover:bg-green-200"
                      : "text-purple-800 bg-purple-100 hover:bg-purple-200"
                  }`}
                >
                  <Luggage className="mr-2 w-5 h-5" />
                  Add Baggage
                  {isAllBaggageSelected() && (
                    <CheckCircle className="ml-2 w-5 h-5 text-green-600" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsMealModalOpen(true);
                    fetchMealDetails();
                    setActivePassengerTab(0);
                  }}
                  className={`flex justify-center items-center px-4 py-3 rounded-lg transition-colors ${
                    isAllMealsSelected()
                      ? "text-green-800 bg-green-100 hover:bg-green-200"
                      : "text-orange-800 bg-orange-100 hover:bg-orange-200"
                  }`}
                >
                  <Restaurant className="mr-2 w-5 h-5" />
                  Add Meals
                  {isAllMealsSelected() && (
                    <CheckCircle className="ml-2 w-5 h-5 text-green-600" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsSeatModalOpen(true);
                    fetchSeatDetails();
                    setActiveSeatTab(0);
                  }}
                  className={`flex justify-center items-center px-4 py-3 rounded-lg transition-colors ${
                    isAllSeatsSelected()
                      ? "text-green-800 bg-green-100 hover:bg-green-200"
                      : "text-indigo-800 bg-indigo-100 hover:bg-indigo-200"
                  }`}
                >
                  <Chair className="mr-2 w-5 h-5" />
                  Select Seats
                  {isAllSeatsSelected() && (
                    <CheckCircle className="ml-2 w-5 h-5 text-green-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <PriceSidebar
            pricingInfo={pricingInfo}
            pricingResponse={pricingResponse}
            formatCurrency={formatCurrency}
            formatNumber={formatNumber}
            totalTax={totalTax}
            selectedBaggage={selectedBaggage}
            selectedMeals={selectedMeals}
            selectedSeats={selectedSeats}
            handleProceedToBooking={handleProceedToBooking}
            getSegmentPath={getSegmentPath}
            charges={charges}
            totalPrice={totalPrice}
          />
        </div>
      </div>
      <Ancillaries
        isBaggageModalOpen={isBaggageModalOpen}
        setIsBaggageModalOpen={setIsBaggageModalOpen}
        passengers={passengers}
        activePassengerTab={activePassengerTab}
        setActivePassengerTab={setActivePassengerTab}
        activeJourneyTab={activeJourneyTab}
        setActiveJourneyTab={setActiveJourneyTab}
        baggageDetails={baggageDetails}
        selectedBaggage={selectedBaggage}
        setSelectedBaggage={setSelectedBaggage}
        formatCurrency={formatCurrency}
        isMealModalOpen={isMealModalOpen}
        setIsMealModalOpen={setIsMealModalOpen}
        mealDetails={mealDetails}
        activeMealSegmentTab={activeMealSegmentTab}
        setActiveMealSegmentTab={setActiveMealSegmentTab}
        selectedSeats={selectedSeats}
        setSelectedSeats={setSelectedSeats}
        setActiveSeatTab={setActiveSeatTab}
        selectedMeals={selectedMeals}
        setSelectedMeals={setSelectedMeals}
        isSeatModalOpen={isSeatModalOpen}
        setIsSeatModalOpen={setIsSeatModalOpen}
        autoSelectedSeats={autoSelectedSeats}
        activeSeatTab={activeSeatTab}
        seatDetails={seatDetails}
      />
    </div>
  );
};

export default BasicFlightPricing;
